import React, {useCallback, useEffect, useRef} from 'react';
import LOGO from './logo.svg';
import './App.css';
import {ZoomMtg} from '@zoom/meetingsdk';
import {generateSignature} from './Signature';
import axios from 'axios';

ZoomMtg.preLoadWasm();
ZoomMtg.prepareWebSDK();
// loads language files, also passes any error messages to the ui
ZoomMtg.i18n.load('en-US');
ZoomMtg.i18n.reload('en-US');

function toTitleCase(value) {
  return value.replace(/\b\w/g, (char) => char.toUpperCase());
}

function getRawQueryParam(search, key) {
  if (!search) {
    return null;
  }

  const query = search.startsWith('?') ? search.slice(1) : search;
  const pairs = query.split('&');

  for (const pair of pairs) {
    if (!pair) {
      continue;
    }

    const separatorIndex = pair.indexOf('=');
    const rawKey = separatorIndex === -1 ? pair : pair.slice(0, separatorIndex);
    const rawValue = separatorIndex === -1 ? '' : pair.slice(separatorIndex + 1);

    if (decodeURIComponent(rawKey) !== key) {
      continue;
    }

    try {
      return decodeURIComponent(rawValue);
    } catch {
      return rawValue;
    }
  }

  return null;
}

function App() {
  const sdkKey = process.env.REACT_APP_ZOOM_MEETING_SDK_KEY;
  const userEmail = '';
  const registrantToken = '';
  const zakToken = '';
  const leaveUrl = process.env.REACT_APP_LEAVE_URL;
  const homeUrl = process.env.REACT_APP_HOME_URL;
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  const meetingArgs = useRef({});
  const encryptedLink = useRef('');
  const hasActiveMeeting = useRef(false);

  const updateMeetingTopic = useCallback(() => {
    const meetingTopic = document.getElementById('meeting-topic');

    if (meetingTopic && meetingArgs.current.meeting_topic) {
      meetingTopic.textContent = meetingArgs.current.meeting_topic;
    }
  }, []);

  const exit = useCallback(() => {
    if (homeUrl) {
      window.location.replace(homeUrl);
    }
  }, [homeUrl]);

  const validateMeetingArgs = useCallback((args) => {
    return sdkKey &&
      leaveUrl &&
      args.meeting_number &&
      args.meeting_pwd &&
      args.meeting_email &&
      (
        args.meeting_role != null &&
        [0, 1, '0', '1'].includes(args.meeting_role)
      ) &&
      args.meeting_lang &&
      args.meeting_topic &&
      args.display_name &&
      (
        args.meeting_china != null &&
        [0, 1, '0', '1'].includes(args.meeting_china)
      ) &&
      args.request_id;
  }, [leaveUrl, sdkKey]);

  const populateMeetingArgs = useCallback(async () => {
    const link = getRawQueryParam(window.location.search, 'cdtl');

    meetingArgs.current = {};

    if (!link || !baseApiUrl) {
      encryptedLink.current = '';
      return false;
    }

    encryptedLink.current = link;

    console.log({link});

    const decryption = await axios.post(`${baseApiUrl}/decrypt`, {
      value: link
    });
    const queryParams = new URLSearchParams(decryption.data.data);

    queryParams.forEach((value, key) => {
      meetingArgs.current[key] = value;
    });

    return true;
  }, [baseApiUrl]);

  const startMeeting = useCallback((signature) => {
    const navTool = document.getElementById('nav-tool');
    const meetingRoot = document.getElementById('zmmtg-root');

    if (navTool) {
      navTool.style.display = 'none';
    }

    if (meetingRoot) {
      meetingRoot.style.display = 'block';
    }

    ZoomMtg.init({
      leaveUrl: `${leaveUrl}${encryptedLink.current ? `?link=${encodeURIComponent(encryptedLink.current)}` : ''}`,
      patchJsMedia: true,
      leaveOnPageUnload: true,
      success: () => {
        ZoomMtg.join({
          signature,
          meetingNumber: meetingArgs.current.meeting_number,
          passWord: meetingArgs.current.meeting_pwd,
          userName: toTitleCase(meetingArgs.current.display_name),
          userEmail,
          tk: registrantToken,
          zak: zakToken,
          success: () => {
            hasActiveMeeting.current = true;
          },
          error: (error) => {
            console.log(error);
          }
        });
      },
      error: (error) => {
        console.log(error);
      },
      disableInvite: true,
      showMeetingHeader: true
    });
  }, [leaveUrl, registrantToken, userEmail, zakToken]);

  const getSignature = useCallback(async () => {
    try {
      const hasLink = await populateMeetingArgs();

      if (!hasLink) {
        return;
      }

      if (validateMeetingArgs(meetingArgs.current)) {
        const validationEndPoint = `/user/request/meeting-link/validate?link=${encodeURIComponent(encryptedLink.current)}`;
        const validateLink = await axios.get(baseApiUrl + validationEndPoint);

        if(!validateLink.data.status) {
          alert(validateLink.data.message);

          return exit();
        }
        
        const signature = await generateSignature(
          meetingArgs.current.meeting_number, 
          Number(meetingArgs.current.meeting_role)
        );
        startMeeting(signature);
      } else {
        alert('Ungültiger Link');
        exit();
      }
    } catch (error) {
      console.log(error);
      alert(error?.response?.data?.message || 'Meeting konnte nicht gestartet werden.');
      // exit();
    }
  }, [baseApiUrl, exit, populateMeetingArgs, startMeeting, validateMeetingArgs]);

  useEffect(() => {
    void getSignature().then(updateMeetingTopic);
  }, [getSignature, updateMeetingTopic]);

  useEffect(() => {
    const joinButtonInterval = window.setInterval(() => {
      const button = document.querySelector('.joinWindowBtn');

      if (button) {
        button.innerHTML = 'Beitreten';
        button.title = 'Beitreten';
        window.clearInterval(joinButtonInterval);
      }
    }, 2000);

    return () => {
      window.clearInterval(joinButtonInterval);

      if (!hasActiveMeeting.current) {
        return;
      }

      ZoomMtg.leaveMeeting({
        confirm: true,
        success: () => {
          hasActiveMeeting.current = false;
        }
      });
    };
  }, []);

  return (
    <div className="App bg-whi">
      <nav id="nav-tool" className="navbar navbar-dark fixed-top bg-pri">
        <div className="container-fluid bg-pri">
            <div className="d-flex align-items-center w-100">
                <div className="logo">
                    <img src={LOGO} alt="" id="dol-logo"/>
                </div>
                <div className="">
                    <a className="navbar-brand !h-fit" id="header-text" href={leaveUrl}>Dolmetscher</a>
                </div>
            </div>
        </div>
      </nav>
      <section className="meeting-details">
        <p className="color-pri f-weight-900 f-size-3" id="meeting-topic">{"Dolmetsche Meeting"}</p>
      </section>
      <main style={{display: 'none'}}>
        <h1>Zoom Meeting SDK Sample React</h1>

        <button onClick={getSignature}>Join Meeting</button>
      </main>
    </div>
  );
}

export default App;
